import React, { useRef, useEffect, useCallback, useState } from 'react';

import { Description, Field, Label, Textarea as HeadlessTextarea } from '@headlessui/react';
import { isEmpty } from 'lodash';

import cn from '~/utils/cn';

export interface TextAreaProps {
  /**
   * Whether or not to disable the textarea.
   */
  disabled?: boolean;
  /**
   * Whether the textarea has a validation error.
   */
  invalid?: boolean;
  /**
   * Whether the textarea can be resized vertically.
   */
  resizable?: boolean;
  /**
   * The name to use when submitting an HTML form.
   */
  name?: string;
  /**
   * The initial value for the textarea.
   */
  defaultValue?: string;
  /**
   * The controlled value of the textarea.
   */
  value?: string;
  /**
   * Handler to call when the textarea value changes.
   */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /**
   * Handler to call when a key is pressed down in the textarea.
   */
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /**
   * The number of visible lines in the textarea.
   */
  rows?: number;
  /**
   * Additional CSS classes to apply to the textarea.
   */
  className?: string;
  /**
   * Placeholder text for the textarea
   */
  placeholder?: string;
  /**
   * Label text for the textarea
   */
  label?: string;
  /**
   * Description text for the textarea
   */
  description?: string;
  /**
   * Error message to display when invalid is true
   */
  errorMessage?: string;
  /**
   * Icon component to display alongside the textarea
   */
  icon?: React.ReactNode;
  /**
   * Position of the icon (left or right)
   */
  iconPosition?: 'left' | 'right';
  /**
   * Whether to automatically expand the textarea as the user types
   */
  autoResize?: boolean;
  /**
   * Maximum height of the textarea in pixels when autoResize is true
   */
  maxHeight?: number;
}

const TextArea: React.FC<TextAreaProps> = ({
  disabled = false,
  invalid = false,
  resizable = true,
  name,
  defaultValue,
  value,
  onChange,
  rows = 1,
  className = '',
  placeholder,
  label,
  description,
  errorMessage,
  onKeyDown,
  autoResize = false,
  maxHeight,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [minHeight, setMinHeight] = useState<number>(0);

  // Function to adjust the height of the textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !autoResize) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (use either scrollHeight or minHeight, whichever is greater)
    const newHeight = Math.max(textarea.scrollHeight, minHeight);

    // Apply maxHeight limit if specified
    const finalHeight = maxHeight ? Math.min(newHeight, maxHeight) : newHeight;
    textarea.style.height = `${finalHeight}px`;

    // Show scrollbar if content exceeds maxHeight
    textarea.style.overflowY = maxHeight && newHeight > maxHeight ? 'auto' : 'hidden';
  }, [minHeight, autoResize, maxHeight]);

  // Handle controlled component case
  useEffect(() => {
    if (isEmpty(value)) {
      setTimeout(adjustHeight, 0);
    }
  }, [value, adjustHeight]);

  // Handle uncontrolled component case with defaultValue
  useEffect(() => {
    if (isEmpty(defaultValue) && textareaRef.current) {
      // Wait for the next tick to ensure the defaultValue is applied
      setTimeout(adjustHeight, 0);
    }
  }, [defaultValue, adjustHeight]);

  // Initial setup after mount
  useEffect(() => {
    if (!textareaRef.current) return;

    const lineHeight = parseInt(getComputedStyle(textareaRef.current).lineHeight);
    setMinHeight(rows > 1 ? lineHeight * rows : lineHeight);
    setTimeout(adjustHeight, 0);

    // Set initial minimum height based on rows prop
    if (textareaRef.current && rows > 1) {
      const paddingTop = parseInt(getComputedStyle(textareaRef.current).paddingTop);
      const paddingBottom = parseInt(getComputedStyle(textareaRef.current).paddingBottom);
      const minHeightWithRows = lineHeight * rows + paddingTop + paddingBottom;

      if (minHeightWithRows > minHeight) {
        textareaRef.current.style.minHeight = `${minHeightWithRows}px`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Custom onChange handler to adjust height
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    adjustHeight();
    onChange?.(e);
  };

  // Handle keydown events (specifically for Enter key)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      // Use setTimeout to let the new line be added before adjusting height
      setTimeout(adjustHeight, 0);
    }
    onKeyDown?.(e);
  };

  // Handle paste events
  const handlePaste = () => {
    // Use setTimeout to let the paste operation complete before adjusting height
    setTimeout(adjustHeight, 0);
  };

  return (
    <Field disabled={disabled} className="w-full">
      {label && <Label className="text-grey-700 mb-1 block text-sm font-medium">{label}</Label>}

      <div className="relative">
        <HeadlessTextarea
          ref={textareaRef}
          name={name}
          defaultValue={defaultValue}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={rows}
          placeholder={placeholder}
          className={cn(
            'border-grey-300 block w-full rounded-lg p-12 focus:border-blue-500 focus:ring-blue-500 sm:text-sm',
            {
              'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500': invalid,
              'cursor-not-allowed opacity-50': disabled,
              'resize-none': !resizable,
              'overflow-hidden': autoResize, // Hide scrollbar for auto-resize
            },
            className
          )}
        />
      </div>

      {description && !invalid && <Description className="text-grey-500 mt-1 text-sm">{description}</Description>}

      {invalid && errorMessage && <Description className="mt-1 text-sm text-red-600">{errorMessage}</Description>}
    </Field>
  );
};

export default TextArea;
